import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { ApiError } from '@/auth/api';
import { PrimaryButton } from '@/components/PrimaryButton';
import { BuyerSelector } from '@/features/customers/components/BuyerSelector';
import type { CustomerItem } from '@/features/customers/types';
import { reserveInternalDevice } from '@/features/operations/api';
import { formatHistoryTimestamp } from '@/features/operations/labels';
import { usePriceDisplay } from '@/features/operations/PriceDisplayProvider';
import type { DeviceDetail } from '@/features/operations/types';

export function ReserveDeviceDialog({
  open,
  token,
  device,
  onClose,
  onReserved,
}: {
  open: boolean;
  token: string;
  device: Pick<DeviceDetail, 'id' | 'brand' | 'model' | 'salePriceMinor'>;
  onClose: (stale: boolean) => void;
  onReserved: (device: DeviceDetail) => void;
}) {
  const { formatNullablePrice } = usePriceDisplay();
  const [buyer, setBuyer] = useState<CustomerItem | null>(null);
  const [saving, setSaving] = useState(false);
  const [stale, setStale] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const expiresAt = new Date(Date.now() + 3 * 24 * 60 * 60 * 1000);

  const close = () => {
    if (saving) return;
    onClose(stale);
    setBuyer(null);
    setStale(false);
    setError(null);
  };

  const reserve = async () => {
    if (!buyer) return;
    setSaving(true);
    setError(null);
    try {
      const result = await reserveInternalDevice(token, device.id, buyer.id);
      setBuyer(null);
      onReserved(result);
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 409) setStale(true);
      else setError('No pudimos reservar el equipo. Revisá la cuenta e intentá de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
      <DialogTitle>
        Reservar {device.brand} {device.model}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {stale ? (
            <Alert severity="error">
              El equipo ya no está disponible. Cerrá el diálogo para ver su estado actual.
            </Alert>
          ) : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
          <Typography variant="body2">
            La reserva no tiene costo, dura 3 días y queda al precio de{' '}
            <strong>{formatNullablePrice(device.salePriceMinor)}</strong>. Vence el{' '}
            <strong>{formatHistoryTimestamp(expiresAt.toISOString())}</strong>.
          </Typography>
          <BuyerSelector token={token} buyer={buyer} onChange={setBuyer} requirePhone />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" disabled={saving} onClick={close}>
          {stale ? 'Cerrar' : 'Cancelar'}
        </Button>
        {!stale ? (
          <PrimaryButton disabled={saving || !buyer} onClick={() => void reserve()}>
            {saving ? 'Reservando...' : 'Confirmar reserva'}
          </PrimaryButton>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
